/**
 * Money — the shared owner / Project Manager finance workspace.
 *
 * Approval authorises spending, a transfer records money sent and a payment
 * records money actually paid. The page never calls an approved expense "spent
 * money": only payment events move a recorded balance.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import {
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { apiGet, apiPost } from '../utils/api';
import { getApiUrl } from '../api/config';
import { useAuth } from '../contexts/AuthContext';

type Totals = {
  funding_sent?: number;
  received_by_field_team?: number;
  confirmed_received?: number;
  opening_balance?: number;
  closing_balance?: number;
  current_balance?: number;
  reported_expenses?: number;
  paid_from_float?: number;
  paid_owner_direct?: number;
  refunded_from_float?: number;
  recorded_payments?: number;
  awaiting_review?: number;
  pending_expenses?: number;
  pending_approval_amount?: number;
  pending_approval_count?: number;
  disputed_expenses?: number;
  outstanding_payments?: number;
  recorded_balance?: number;
  unattributed_float_payments?: number;
  unattributed_float_payments_all_time?: number;
  unrecorded_payment_expenses?: number;
  by_currency?: Array<{
    currency?: string | null;
    funding_sent?: number;
    received_by_field_team?: number;
    recorded_payments?: number;
    recorded_spending?: number;
  }>;
};

type Project = { id?: number; name?: string | null };

type Expense = {
  id: number;
  spent_on?: string;
  category?: string;
  amount?: number;
  currency?: string;
  description?: string;
  supplier_name?: string;
  spent_by?: string | number;
  spent_by_id?: number;
  spent_by_user_id?: number;
  review_assigned_to_name?: string | null;
  field?: string;
  job?: string;
  review_status?: string;
  review_version?: number;
  review_note?: string;
  review_assigned_to_user_id?: number;
  paid_amount?: number;
  outstanding_amount?: number;
  payment_status?: string;
  receipt_photo_urls?: string[];
  field_plot_id?: number;
  field_diary_id?: number;
  assignment_id?: number;
};

type Payment = {
  id: number;
  amount?: number;
  currency?: string;
  method?: string;
  paid_at?: string;
  event_type?: string;
  funding_source?: string;
  payer_name?: string;
  payer_user_id?: number | null;
  reverses_event_id?: number | null;
  recorded_by_name?: string;
  recorded_by_user_id?: number;
  reference?: string;
};

type WorkerPaymentEvent = {
  allocation_id?: number;
  id?: number;
  event_type?: string;
  amount?: number;
  reverses_allocation_id?: number | null;
};

type LabourWorker = {
  worker_record_id: number;
  worker_name?: string;
  wage_amount?: number | null;
  currency?: string | null;
  payment_needs_reconciliation?: boolean;
  payment_events?: WorkerPaymentEvent[];
};

type FundingRequest = {
  id: number;
  category?: string;
  amount?: number;
  authorized_amount?: number;
  remaining_amount?: number;
  reason?: string;
  status?: string;
  created_at?: string;
  period_month?: string;
  requested_by?: string;
  requested_by_user_id?: number;
  needed_by?: string | null;
  field_plot_id?: number | null;
  assignment_id?: number | null;
  field_name?: string | null;
  operation?: string | null;
  attachment_urls?: Array<string | { url?: string; name?: string; mime_type?: string }> | null;
};

type Detail = {
  expense: Expense & { farm_wide?: boolean; legacy_without_payment?: boolean };
  payments: Payment[];
  workers?: LabourWorker[];
  worker_allocations_available?: boolean;
  lots: any[];
  audit: any[];
};

const money = (value?: number, currency = 'RWF') =>
  `${Number(value || 0).toLocaleString()} ${currency}`;
const moneyMinor = (value: string): string | null => {
  const match = /^(\d{1,12})(?:\.(\d{1,2}))?$/.exec(value.trim());
  if (!match) return null;
  const cents = `${match[1]}${(match[2] || '').padEnd(2, '0')}`.replace(/^0+(?=\d)/, '');
  return cents || '0';
};
const addMinor = (left: string, right: string): string => {
  let i = left.length - 1;
  let j = right.length - 1;
  let carry = 0;
  let result = '';
  while (i >= 0 || j >= 0 || carry) {
    const sum = (i >= 0 ? Number(left[i--]) : 0) + (j >= 0 ? Number(right[j--]) : 0) + carry;
    result = String(sum % 10) + result;
    carry = Math.floor(sum / 10);
  }
  return result.replace(/^0+(?=\d)/, '') || '0';
};
const day = (value?: string) => (value ? new Date(value).toLocaleDateString() : '—');

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  reviewed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  disputed: 'bg-red-50 text-red-700 border-red-200',
};

const PAGE_SIZE = 25;
const localDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const MoneyPage: React.FC = () => {
  const { user } = useAuth();
  const isAssignedReviewer = ['agronomist', 'field_facilitator'].includes(user?.role || '');
  const canRecordTransfer = ['owner', 'project_manager'].includes(user?.role || '');
  const [searchParams] = useSearchParams();
  const [totals, setTotals] = useState<Totals>({});
  const [project, setProject] = useState<Project | null>(null);
  const [currency, setCurrency] = useState('RWF');
  const [transfers, setTransfers] = useState<any[]>([]);
  const [fundingRequests, setFundingRequests] = useState<FundingRequest[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [pendingExpenses, setPendingExpenses] = useState<Expense[]>([]);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [month, setMonth] = useState(searchParams.get('month') || '');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [category, setCategory] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [fieldPlotIdFilter, setFieldPlotIdFilter] = useState('');
  const [assignmentIdFilter, setAssignmentIdFilter] = useState('');
  const [workerIdFilter, setWorkerIdFilter] = useState('');
  const [recipientIdFilter, setRecipientIdFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [requestBusyId, setRequestBusyId] = useState<number | null>(null);
  const [decliningRequest, setDecliningRequest] = useState<FundingRequest | null>(null);
  const [declineNote, setDeclineNote] = useState('');
  const [detail, setDetail] = useState<Detail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [reviewers, setReviewers] = useState<any[]>([]);
  const [reviewerId, setReviewerId] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('cash');
  const [payType, setPayType] = useState<'payment' | 'refund'>('payment');
  const [paySource, setPaySource] = useState('float');
  const [payPayerId, setPayPayerId] = useState('');
  const [payReference, setPayReference] = useState('');
  const [reversesEventId, setReversesEventId] = useState('');
  const [workerAllocationAmounts, setWorkerAllocationAmounts] = useState<Record<number, string>>({});
  const [paymentRequestId, setPaymentRequestId] = useState('');
  const [paymentFingerprint, setPaymentFingerprint] = useState('');
  const [legacyPayerId, setLegacyPayerId] = useState('');
  const [legacyReason, setLegacyReason] = useState('');
  const [showTransfer, setShowTransfer] = useState(false);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferRecipient, setTransferRecipient] = useState('');
  const [transferMethod, setTransferMethod] = useState('mobile_money');
  const [transferReference, setTransferReference] = useState('');
  const [transferDate, setTransferDate] = useState(localDate);
  const [transferRequestId, setTransferRequestId] = useState('');
  const [transferIdempotencyKey, setTransferIdempotencyKey] = useState('');
  const [transferFingerprint, setTransferFingerprint] = useState('');
  const reportPeriodLabel = fromDate && toDate ? `${fromDate}_to_${toDate}` : month || 'all-time';

  const buildExpenseParams = useCallback((offset: number, limit: number) => {
    const params = new URLSearchParams();
    if (fromDate || toDate) {
      if (fromDate && toDate && fromDate <= toDate) {
        params.set('from', fromDate);
        params.set('to', toDate);
      }
    } else if (month) {
      params.set('month', month);
    } else {
      params.set('period', 'all');
    }
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (paymentStatus) params.set('payment_status', paymentStatus);
    if (fieldFilter.trim()) params.set('field', fieldFilter.trim());
    if (/^[1-9]\d*$/.test(fieldPlotIdFilter)) params.set('field_plot_id', fieldPlotIdFilter);
    if (/^[1-9]\d*$/.test(assignmentIdFilter)) params.set('assignment_id', assignmentIdFilter);
    if (/^[1-9]\d*$/.test(workerIdFilter)) params.set('worker_record_id', workerIdFilter);
    if (/^[1-9]\d*$/.test(recipientIdFilter)) params.set('recipient_user_id', recipientIdFilter);
    if (currencyFilter) params.set('currency', currencyFilter);
    if (query.trim()) params.set('q', query.trim());
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return params;
  }, [fromDate, toDate, month, status, category, paymentStatus, fieldFilter, fieldPlotIdFilter, assignmentIdFilter, workerIdFilter, recipientIdFilter, currencyFilter, query]);

  const load = useCallback(async () => {
    if ((fromDate || toDate) && (!fromDate || !toDate || fromDate > toDate)) {
      setError('Choose a valid start and end date for the report.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (isAssignedReviewer) {
        const [pendingList, disputedList] = await Promise.all([
          apiGet<any>('/api/owner/expenses?status=pending&period=all&assigned=me&limit=100'),
          apiGet<any>('/api/owner/expenses?status=disputed&period=all&assigned=me&limit=100'),
        ]);
        const queue = [
          ...(Array.isArray(pendingList?.expenses) ? pendingList.expenses : []),
          ...(Array.isArray(disputedList?.expenses) ? disputedList.expenses : []),
        ];
        setPendingExpenses(queue);
        setExpenses([]);
        setTotal(queue.length);
        setTotals({});
        setTransfers([]);
        setApprovalError(null);
        return;
      }

      const params = buildExpenseParams(page * PAGE_SIZE, PAGE_SIZE);

      // The approval queue is always all-time: a pending expense must stay
      // discoverable even when the user drilled into another month.
      const budgetParams = new URLSearchParams();
      if (fromDate && toDate) {
        budgetParams.set('from', fromDate);
        budgetParams.set('to', toDate);
      } else if (month) budgetParams.set('month', month);
      else budgetParams.set('period', 'all');
      const [budget, list] = await Promise.all([
        apiGet<any>(`/api/owner/budget?${budgetParams.toString()}`),
        apiGet<any>(`/api/owner/expenses?${params.toString()}`),
      ]);
      setTotals(budget?.totals || {});
      setProject(budget?.project || null);
      setTransfers(Array.isArray(budget?.disbursements) ? budget.disbursements : []);
      setFundingRequests(Array.isArray(budget?.requests) ? budget.requests : []);
      setCurrency(budget?.currency || 'RWF');
      setExpenses(Array.isArray(list?.expenses) ? list.expenses : []);
      setTotal(Number(list?.total || 0));
      // Keep the decision queue independent from the filtered ledger. A
      // failure loading one view must not hide the rest of Money, and disputed
      // entries must remain visible alongside new submissions.
      try {
        const [pendingList, disputedList] = await Promise.all([
          apiGet<any>('/api/owner/expenses?status=pending&period=all&limit=100'),
          apiGet<any>('/api/owner/expenses?status=disputed&period=all&limit=100'),
        ]);
        const queue = [
          ...(Array.isArray(pendingList?.expenses) ? pendingList.expenses : []),
          ...(Array.isArray(disputedList?.expenses) ? disputedList.expenses : []),
        ];
        setPendingExpenses(queue);
        setApprovalError(null);
      } catch (queueError: any) {
        setApprovalError(queueError?.message || 'The approval queue could not be loaded.');
      }
    } catch (e: any) {
      setError(e?.message || 'Money is unavailable.');
    } finally {
      setLoading(false);
    }
  }, [buildExpenseParams, month, fromDate, toDate, page, isAssignedReviewer]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const focus = searchParams.get('focus');
    const targetId = focus === 'expense-approvals'
      ? 'expense-approvals'
      : focus === 'operations-cash-requests'
        ? 'operations-cash-requests'
        : null;
    if (!targetId || loading) return;
    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }, [loading, searchParams]);

  const pending = useMemo(
    () => pendingExpenses.filter((e) => e.review_status === 'pending' || e.review_status === 'disputed'),
    [pendingExpenses]
  );

  const decide = async (expense: Expense, nextStatus: string) => {
    if (nextStatus !== 'reviewed' && reason.trim().length < 5) {
      setMessage('A reason of at least 5 characters is required to dispute or reopen.');
      return;
    }
    setBusyId(expense.id);
    try {
      await apiPost(`/api/owner/money/expenses/${expense.id}/review`, {
        status: nextStatus,
        note: reason.trim() || undefined,
        expected_version: expense.review_version,
      });
      setMessage('Decision recorded. No money moved.');
      setReason('');
      setDetail(null);
      await load();
    } catch (e: any) {
      setMessage(e?.message || 'The decision could not be recorded.');
    } finally {
      setBusyId(null);
    }
  };

  const decideFundingRequest = async (request: FundingRequest, decision: 'approved' | 'declined', note?: string) => {
    setRequestBusyId(request.id);
    try {
      await apiPost(`/api/owner/requests/${request.id}/decision`, {
        decision,
        note: note?.trim() || undefined,
      });
      setMessage(decision === 'approved'
        ? 'Cash request approved. This authorises spending but does not record a transfer.'
        : 'Cash request declined.');
      setDecliningRequest(null);
      setDeclineNote('');
      await load();
    } catch (e: any) {
      setMessage(e?.message || 'The cash request decision could not be saved.');
    } finally {
      setRequestBusyId(null);
    }
  };

  const openDetail = async (expense: Expense) => {
    setDetailLoading(true);
    setDetail(null);
    setReason('');
    setPayAmount('');
    setPayType('payment');
    setPaySource('float');
    setReversesEventId('');
    setWorkerAllocationAmounts({});
    setPaymentRequestId('');
    setPaymentFingerprint('');
    setLegacyPayerId('');
    setLegacyReason('');
    try {
      const d = await apiGet<Detail>(`/api/owner/expenses/${expense.id}`);
      const reviewerList = isAssignedReviewer
        ? { reviewers: [] }
        : await apiGet<any>('/api/owner/money/reviewers').catch(() => ({ reviewers: [] }));
      setDetail(d);
      setReviewers(Array.isArray(reviewerList?.reviewers) ? reviewerList.reviewers : []);
      setReviewerId(d?.expense?.review_assigned_to_user_id ? String(d.expense.review_assigned_to_user_id) : '');
      setPayPayerId(String(d?.expense?.spent_by_id ?? d?.expense?.spent_by ?? user?.id ?? ''));
    } catch (e: any) {
      setMessage(e?.message || 'The expense detail is unavailable.');
    } finally {
      setDetailLoading(false);
    }
  };

  const recordPayment = async () => {
    if (!detail) return;
    const amountCents = moneyMinor(payAmount);
    if (amountCents == null || amountCents === '0') {
      setMessage('Enter a payment amount greater than zero.');
      return;
    }
    if (payType === 'refund' && !reversesEventId) {
      setMessage('Choose the payment this refund reverses.');
      return;
    }
    const selectedPayment = payType === 'refund'
      ? detail.payments.find((payment) => payment.id === Number(reversesEventId))
      : undefined;
    const sourceAllocations = payType === 'refund'
      ? (detail.workers || []).flatMap((worker) => (worker.payment_events || [])
        .filter((event) => event.id === selectedPayment?.id && event.event_type === 'payment')
        .map((event) => ({ worker, event })))
      : [];
    const allocations = Object.entries(workerAllocationAmounts)
      .map(([workerId, value]) => ({
        worker_record_id: Number(workerId),
        amount: value.trim(),
        reverses_allocation_id: payType === 'refund'
          ? sourceAllocations.find((row) => row.worker.worker_record_id === Number(workerId))?.event.allocation_id
          : undefined,
      }))
      .filter((item) => item.amount.length > 0);
    let allocationCents = '0';
    for (const item of allocations) {
      const cents = moneyMinor(item.amount);
      if (cents == null || cents === '0') {
        setMessage('Worker allocations must be positive amounts with no more than two decimals.');
        return;
      }
      allocationCents = addMinor(allocationCents, cents);
    }
    if (allocations.length && allocationCents !== amountCents) {
      setMessage('Worker allocations must add up exactly to the payment or refund amount.');
      return;
    }
    if (payType === 'refund' && sourceAllocations.length > 0 && allocations.length === 0) {
      setMessage('This payment was allocated to workers. Allocate this refund back to those same worker payment records.');
      return;
    }
    if (allocations.some((item) => payType === 'refund' && !item.reverses_allocation_id)) {
      setMessage('A worker refund must reverse an original worker allocation.');
      return;
    }
    const fingerprint = JSON.stringify({
      expense_id: detail.expense.id,
      amount: payAmount.trim(),
      method: payMethod,
      funding_source: paySource,
      payer_user_id: payPayerId || null,
      event_type: payType,
      reference: payReference.trim(),
      reverses_event_id: payType === 'refund' ? Number(reversesEventId) : null,
      worker_allocations: allocations,
    });
    const requestId = paymentFingerprint === fingerprint && paymentRequestId
      ? paymentRequestId
      : (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setPaymentFingerprint(fingerprint);
    setPaymentRequestId(requestId);
    try {
      await apiPost(`/api/owner/money/expenses/${detail.expense.id}/payments`, {
        amount: payAmount.trim(),
        method: payMethod,
        funding_source: paySource,
        payer_user_id: payPayerId ? Number(payPayerId) : undefined,
        event_type: payType,
        reference: payReference || undefined,
        reverses_event_id: payType === 'refund' ? Number(reversesEventId) : undefined,
        idempotency_key: requestId,
        worker_allocations: allocations,
      });
      setMessage(payType === 'refund' ? 'Refund recorded.' : 'Payment recorded. The balance moved once.');
      await openDetail(detail.expense);
      await load();
    } catch (e: any) {
      setMessage(e?.message || 'The payment could not be recorded.');
    }
  };

  const assignReviewer = async () => {
    if (!detail) return;
    try {
      await apiPost(`/api/owner/money/expenses/${detail.expense.id}/reviewer`, {
        reviewer_user_id: reviewerId ? Number(reviewerId) : null,
      });
      setMessage('Reviewer assignment saved.');
      await openDetail(detail.expense);
    } catch (e: any) {
      setMessage(e?.message || 'The reviewer could not be assigned.');
    }
  };

  const attributeLegacyPayment = async (paymentId: number) => {
    if (!legacyPayerId || legacyReason.trim().length < 5) {
      setMessage('Choose the confirmed payer and provide a reconciliation reason of at least 5 characters.');
      return;
    }
    try {
      await apiPost(`/api/owner/money/payments/${paymentId}/attribute`, {
        payer_user_id: Number(legacyPayerId),
        reason: legacyReason.trim(),
      });
      setMessage('Historical payment attribution saved. No new cash event was created.');
      setLegacyPayerId('');
      setLegacyReason('');
      if (detail) await openDetail(detail.expense);
      await load();
    } catch (e: any) {
      setMessage(e?.message || 'The historical payment could not be reconciled.');
    }
  };

  const openTransfer = async () => {
    setShowTransfer(true);
    try {
      const data = await apiGet<any>('/api/owner/recipients');
      setRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
    } catch { setRecipients([]); }
  };

  const recordTransfer = async () => {
    const amount = moneyMinor(transferAmount);
    if (!amount || amount === '0' || !transferRecipient || !transferDate) {
      setMessage('A transfer needs a positive amount and a named recipient.');
      return;
    }
    const linkedRequest = fundingRequests.find((request) => String(request.id) === transferRequestId);
    if (transferRequestId && (!linkedRequest || linkedRequest.status !== 'approved'
      || Number(linkedRequest.requested_by_user_id) !== Number(transferRecipient))) {
      setMessage('Choose an approved request and send it to the named requester.');
      return;
    }
    const requestRemaining = linkedRequest ? moneyMinor(String(linkedRequest.remaining_amount ?? 0)) : null;
    if (linkedRequest && requestRemaining && (amount.length > requestRemaining.length
      || (amount.length === requestRemaining.length && amount > requestRemaining))) {
      setMessage(`The linked request has only ${money(linkedRequest.remaining_amount, currency)} remaining.`);
      return;
    }
    const fingerprint = JSON.stringify({
      amount: transferAmount.trim(), recipient: transferRecipient, method: transferMethod,
      reference: transferReference.trim(), sent_on: transferDate, funding_request_id: transferRequestId || null,
    });
    const idempotencyKey = transferFingerprint === fingerprint && transferIdempotencyKey
      ? transferIdempotencyKey
      : (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setTransferFingerprint(fingerprint);
    setTransferIdempotencyKey(idempotencyKey);
    try {
      await apiPost('/api/owner/disbursements', {
        amount: transferAmount.trim(),
        recipient_user_id: Number(transferRecipient),
        method: transferMethod,
        sent_on: transferDate,
        reference: transferReference || undefined,
        funding_request_id: transferRequestId ? Number(transferRequestId) : undefined,
        idempotency_key: idempotencyKey,
      });
      setShowTransfer(false);
      setTransferAmount('');
      setTransferReference('');
      setTransferRequestId('');
      setTransferDate(localDate());
      setTransferFingerprint('');
      setTransferIdempotencyKey('');
      setMessage('Transfer recorded. Only the named recipient can confirm receipt.');
      await load();
    } catch (e: any) {
      setMessage(e?.message || 'The transfer could not be recorded.');
    }
  };

  const fetchAllFilteredExpenses = async () => {
    if ((fromDate || toDate) && (!fromDate || !toDate || fromDate > toDate)) {
      throw new Error('Choose a valid start and end date before exporting.');
    }
    const params = buildExpenseParams(0, 200);
    let offset = 0;
    let expectedTotal = Infinity;
    const all: Expense[] = [];
    while (all.length < expectedTotal) {
      params.set('offset', String(offset));
      const pageData = await apiGet<any>(`/api/owner/expenses?${params.toString()}`);
      const batch: Expense[] = Array.isArray(pageData?.expenses) ? pageData.expenses : [];
      expectedTotal = Number(pageData?.total ?? all.length + batch.length);
      if (!batch.length) break;
      all.push(...batch);
      offset += batch.length;
      if (all.length >= expectedTotal) break;
      if (all.length >= 20000) throw new Error('Narrow the filters to export at most 20,000 expenses at a time.');
    }
    return all;
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const data = await fetchAllFilteredExpenses();
        const header = ['Date', 'Description', 'Category', 'Field', 'Job', 'Person', 'Supplier', 'Reported amount', 'Currency', 'Paid amount', 'Outstanding', 'Review status', 'Payment status'];
      const rows = data.map((e) => [
        day(e.spent_on), e.description || '', e.category || '', e.field || 'Farm-wide', e.job || '', e.spent_by || '', e.supplier_name || '',
        e.amount ?? 0, e.currency || '', e.paid_amount ?? 0, e.outstanding_amount ?? 0, e.review_status || 'pending', e.payment_status || 'not_recorded',
      ]);
      const csv = [header, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `expenses-${reportPeriodLabel}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setMessage(`Exported all ${data.length} matching expenses.`);
    } catch (e: any) {
      setMessage(e?.message || 'The full expense export could not be created.');
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const rows = await fetchAllFilteredExpenses();
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text(`Expense report · ${fromDate && toDate ? `${fromDate} to ${toDate}` : month || 'all time'}`, 12, 16);
      doc.setFontSize(9);
      let y = 25;
      for (const e of rows) {
        const text = doc.splitTextToSize(
          `${day(e.spent_on)} · ${e.description || 'Expense'} · ${e.field || 'Farm-wide'} · ${e.job || 'No job'}\n` +
          `${money(e.amount, e.currency || 'Currency unknown')} reported · ${money(e.paid_amount, e.currency || 'Currency unknown')} paid · ${money(e.outstanding_amount, e.currency || 'Currency unknown')} outstanding · ${e.review_status || 'pending'} / ${e.payment_status || 'not recorded'}`,
          186,
        );
        const height = text.length * 4.5 + 3;
        if (y + height > 282) { doc.addPage(); y = 18; }
        doc.text(text, 12, y);
        y += height;
      }
      doc.save(`expenses-${reportPeriodLabel}.pdf`);
      setMessage(`Exported all ${rows.length} matching expenses.`);
    } catch (e: any) {
      setMessage(e?.message || 'The full expense export could not be created.');
    } finally {
      setExporting(false);
    }
  };

  const refundAllocationWorkers = payType === 'refund' && reversesEventId
    ? (detail?.workers || []).filter((worker) => (worker.payment_events || []).some((event) =>
      event.id === Number(reversesEventId) && event.event_type === 'payment'))
    : [];
  const paymentAllocationWorkers = payType === 'refund'
    ? refundAllocationWorkers
    : (detail?.workers || []);

  if (isAssignedReviewer) {
    return (
      <main className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
          <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-black text-slate-900">Expense reviews</h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">
                Review only expenses assigned to you. Approval records a decision; it does not transfer or pay money.
              </p>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-4 w-4" /> Refresh
            </button>
          </header>

          {error && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
              <span>{error}</span>
              <button type="button" onClick={load} className="font-bold underline">Retry</button>
            </div>
          )}
          {message && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" role="status">
              <span>{message}</span>
              <button type="button" onClick={() => setMessage(null)} className="font-bold">Dismiss</button>
            </div>
          )}
          {approvalError && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
              {approvalError}
            </div>
          )}

          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div>
                <h2 className="text-base font-black text-slate-900">Assigned to me</h2>
                <p className="text-xs text-slate-500">Pending and disputed submissions</p>
              </div>
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-black text-amber-800">
                {pending.length} to review
              </span>
            </header>
            {loading ? (
              <p className="px-4 py-8 text-sm text-slate-500">Loading assigned expenses…</p>
            ) : pending.length === 0 ? (
              <p className="px-4 py-8 text-sm text-slate-500">No expenses are currently assigned to you.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {pending.map((expense) => (
                  <li key={expense.id}>
                    <button
                      type="button"
                      onClick={() => openDetail(expense)}
                      className="flex w-full flex-wrap items-center gap-3 px-4 py-4 text-left hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-black text-slate-900">
                          {expense.description || 'Expense'}
                        </p>
                        <p className="mt-1 truncate text-xs text-slate-600">
                          {[expense.field || 'Farm-wide', expense.job, expense.supplier_name, day(expense.spent_on)]
                            .filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <span className={`rounded-full border px-2 py-1 text-xs font-bold ${STATUS_STYLES[expense.review_status || 'pending']}`}>
                        {expense.review_status || 'pending'}
                      </span>
                      <span className="text-sm font-black text-slate-900">
                        {money(expense.amount, expense.currency || currency)}
                      </span>
                      <span className="text-sm font-bold text-emerald-800">Review</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {(detail || detailLoading) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setDetail(null)}>
            <section role="dialog" aria-modal="true" aria-label="Review expense" className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              {detailLoading || !detail ? (
                <p className="text-sm text-slate-500">Loading expense…</p>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-black text-slate-900">{detail.expense.description || 'Expense review'}</h2>
                      <p className="mt-1 text-sm text-slate-600">
                        {[detail.expense.category, detail.expense.field || 'Farm-wide', detail.expense.job, day(detail.expense.spent_on)]
                          .filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <p className="shrink-0 text-lg font-black text-slate-900">
                      {money(detail.expense.amount, detail.expense.currency || currency)}
                    </p>
                  </div>
                  <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
                    <div><dt className="text-xs font-bold text-slate-500">Submitted by</dt><dd>{(detail.expense as any).spent_by_name || detail.expense.spent_by || '—'}{detail.expense.spent_by_id ? ` · User #${detail.expense.spent_by_id}` : ''}</dd></div>
                    <div><dt className="text-xs font-bold text-slate-500">Supplier</dt><dd>{detail.expense.supplier_name || '—'}</dd></div>
                    <div><dt className="text-xs font-bold text-slate-500">Workspace</dt><dd>{project?.name || 'Workspace'}{project?.id ? ` · Project #${project.id}` : ''}</dd></div>
                    <div><dt className="text-xs font-bold text-slate-500">Assigned reviewer</dt><dd>{(detail.expense as any).review_assigned_to_name || (detail.expense as any).review_assigned_to_user_id ? `${(detail.expense as any).review_assigned_to_name || 'User'} · User #${(detail.expense as any).review_assigned_to_user_id || '—'}` : 'Owner / manager queue'}</dd></div>
                    <div><dt className="text-xs font-bold text-slate-500">Already paid</dt><dd>{money(detail.expense.paid_amount, detail.expense.currency || currency)}</dd></div>
                    <div><dt className="text-xs font-bold text-slate-500">Payment record</dt><dd>{detail.expense.payment_status === 'not_recorded' ? 'Not recorded' : 'Recorded'}</dd></div>
                  </dl>
                  {(detail.expense.receipt_photo_urls || []).length > 0 && (
                    <div className="mt-5">
                      <h3 className="text-sm font-black text-slate-900">Receipts and evidence</h3>
                      <ul className="mt-2 space-y-1">
                        {(detail.expense.receipt_photo_urls || []).map((url, index) => (
                          <li key={`${url}-${index}`}>
                            <a href={url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-emerald-800 underline">
                              Open attachment {index + 1}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {detail.expense.review_note && (
                    <p className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Previous review note: {detail.expense.review_note}</p>
                  )}
                  {(detail.expense.review_status === 'pending' || detail.expense.review_status === 'disputed') && (
                    <div className="mt-5 border-t border-slate-100 pt-4">
                      <label htmlFor="assigned-review-reason" className="text-xs font-bold text-slate-600">
                        Note (required to dispute or reopen)
                      </label>
                      <textarea
                        id="assigned-review-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      />
                      <div className="mt-3 flex flex-wrap justify-end gap-2">
                        <button type="button" onClick={() => setDetail(null)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700">Close</button>
                        <button
                          type="button"
                          disabled={busyId === detail.expense.id}
                          onClick={() => decide(detail.expense, 'disputed')}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-800 disabled:opacity-50"
                        >Dispute</button>
                        <button
                          type="button"
                          disabled={busyId === detail.expense.id || Number(detail.expense.spent_by_id) === Number(user?.id)}
                          onClick={() => decide(detail.expense, 'reviewed')}
                          className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                        >Approve</button>
                      </div>
                      {Number(detail.expense.spent_by_id) === Number(user?.id) && (
                        <p className="mt-2 text-right text-xs text-red-700">You cannot approve your own expense.</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black text-slate-900">Money</h1>
            <p className="text-sm text-slate-500">
              {[project?.name, project?.id ? `Project #${project.id}` : null,
                fromDate && toDate ? `${fromDate} to ${toDate}` : month || 'All time']
                .filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="month"
              value={month}
              disabled={!!(fromDate || toDate)}
              onChange={(e) => { setMonth(e.target.value); setPage(0); }}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold"
              aria-label="Report month"
            />
            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setMonth(''); setPage(0); }} aria-label="Report start date" className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm" />
            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setMonth(''); setPage(0); }} aria-label="Report end date" className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm" />
            {(fromDate || toDate) && <button type="button" onClick={() => { setFromDate(''); setToDate(''); setPage(0); }} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600">Clear dates</button>}
            <button type="button" onClick={exportCsv} disabled={exporting} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {exporting ? 'Preparing…' : 'CSV'}
            </button>
            <button type="button" onClick={exportPdf} disabled={exporting} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {exporting ? 'Preparing…' : 'PDF'}
            </button>
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              <ArrowPathIcon className="h-4 w-4" /> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}
        {message && (
          <div className="mb-4 flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800">
            <span>{message}</span>
            <button type="button" className="text-xs font-black" onClick={() => setMessage(null)}>Dismiss</button>
          </div>
        )}

        {Number(totals.unattributed_float_payments_all_time ?? totals.unattributed_float_payments ?? 0) > 0 && (
          <div className="mb-4 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-700" />
            <p><strong>Legacy payments need reconciliation.</strong> {money(totals.unattributed_float_payments_all_time ?? totals.unattributed_float_payments, currency)} has no recorded payer, so it is excluded from individual float balances. Attribute the existing payment below; do not create a duplicate cash event.</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-6">
          {[
            ['Available now', totals.current_balance ?? totals.recorded_balance, BanknotesIcon],
            ['Total received', totals.confirmed_received, CheckCircleIcon],
            ['Cash sent to team', totals.funding_sent, PaperAirplaneIcon],
            ['Expenses reported', totals.reported_expenses, ReceiptPercentIcon],
            [`Pending approval (${totals.pending_approval_count ?? pending.filter((e) => e.review_status === 'pending').length})`, totals.pending_approval_amount ?? totals.pending_expenses, ExclamationTriangleIcon],
            ['Outstanding payments', totals.outstanding_payments, BanknotesIcon],
          ].map(([label, value, Icon]: any) => (
            <div key={label} className="min-h-24 rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 shrink-0 text-emerald-700" />
                <p className="text-[11px] font-bold uppercase text-slate-500">{label}</p>
              </div>
              <p className="mt-2 text-base font-black text-slate-900 sm:text-lg">{money(value, currency)}</p>
            </div>
          ))}
        </div>

        {(totals.by_currency || []).some((item) => item.currency !== currency) && (
          <section className="mt-4 overflow-hidden rounded-lg border border-amber-200 bg-white">
            <header className="border-b border-amber-100 bg-amber-50 px-4 py-3">
              <h2 className="text-sm font-black text-amber-950">Historical activity in other currencies</h2>
              <p className="mt-1 text-xs text-amber-900">Kept separate from the workspace balance; no conversion has been applied.</p>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-xs">
                <thead className="text-slate-500"><tr><th className="px-4 py-2">Currency</th><th className="px-4 py-2">Sent</th><th className="px-4 py-2">Received</th><th className="px-4 py-2">Payments net</th><th className="px-4 py-2">Expenses reported</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(totals.by_currency || []).filter((item) => item.currency !== currency).map((item, index) => {
                    const code = item.currency || 'Unclassified';
                    return <tr key={`${code}-${index}`}>
                      <td className="px-4 py-3 font-bold text-slate-800">{code}</td>
                      <td className="px-4 py-3">{money(item.funding_sent, item.currency || 'Currency unknown')}</td>
                      <td className="px-4 py-3">{money(item.received_by_field_team, item.currency || 'Currency unknown')}</td>
                      <td className="px-4 py-3">{money(item.recorded_payments, item.currency || 'Currency unknown')}</td>
                      <td className="px-4 py-3">{money(item.recorded_spending, item.currency || 'Currency unknown')}</td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section id="operations-cash-requests" className="mt-6 scroll-mt-20 overflow-hidden rounded-lg border border-slate-200 bg-white">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div>
              <h2 className="text-base font-black text-slate-900">Operations cash requests</h2>
              <p className="text-xs text-slate-500">Money the agronomy team asked for to run farm operations, awaiting an owner or manager decision.</p>
            </div>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-black text-amber-800">
              {fundingRequests.filter((request) => request.status === 'pending').length} pending
            </span>
          </header>
          {loading ? (
            <p className="px-4 py-6 text-sm text-slate-500">Loading money requests…</p>
          ) : fundingRequests.filter((request) => request.status === 'pending').length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-500">No pending requests for additional money.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {fundingRequests.filter((request) => request.status === 'pending').map((request) => (
                <li key={request.id} className="flex flex-wrap items-start gap-3 px-4 py-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <p className="text-sm font-black capitalize text-slate-900">
                        {(request.category || 'other').replace(/_/g, ' ')}
                      </p>
                      <p className="text-sm font-black text-slate-900">{money(request.amount, currency)}</p>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{request.reason || 'No reason provided.'}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {[request.requested_by, request.period_month, day(request.created_at)]
                        .filter(Boolean).join(' · ')}
                    </p>
                    {(request.field_name || request.operation || request.needed_by) && (
                      <p className="mt-1 text-xs font-semibold text-slate-600">
                        {[request.field_name, request.operation, request.needed_by ? `Needed by ${day(request.needed_by)}` : null]
                          .filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {!!request.attachment_urls?.length && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {request.attachment_urls.map((attachment, index) => {
                          const url = typeof attachment === 'string' ? attachment : attachment.url || '';
                          if (!url) return null;
                          const href = url.startsWith('/') ? getApiUrl(url) : url;
                          return (
                            <a key={`${request.id}-attachment-${index}`} href={href} target="_blank" rel="noreferrer"
                              className="inline-flex min-h-8 items-center rounded-md border border-emerald-200 px-2 text-xs font-bold text-emerald-800 hover:bg-emerald-50">
                              {typeof attachment === 'string' ? `Evidence ${index + 1}` : attachment.name || `Evidence ${index + 1}`}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={requestBusyId === request.id}
                      onClick={() => setDecliningRequest(request)}
                      className="rounded-md border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >Decline</button>
                    <button
                      type="button"
                      disabled={requestBusyId === request.id}
                      onClick={() => void decideFundingRequest(request, 'approved')}
                      className="rounded-md bg-emerald-700 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-800 disabled:opacity-50"
                    >{requestBusyId === request.id ? 'Saving…' : 'Approve'}</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section id="expense-approvals" className="mt-6 scroll-mt-20 rounded-xl border border-slate-200 bg-white">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div>
              <h2 className="text-base font-black text-slate-900">Approvals</h2>
              <p className="text-xs text-slate-500">Pending and disputed expenses. The queue stays visible even at zero.</p>
            </div>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-black text-amber-700">
              {pending.length} waiting
            </span>
          </header>
          {approvalError && (
            <div className="flex items-center justify-between gap-3 border-b border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
              <span>{approvalError}</span>
              <button type="button" onClick={load} className="shrink-0 font-bold underline">Retry</button>
            </div>
          )}
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : pending.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">No expense is waiting for a decision.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {pending.map((expense) => (
                <li key={expense.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <button type="button" onClick={() => openDetail(expense)} className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-black text-slate-900">
                      {expense.description} · {money(expense.amount, expense.currency || currency)}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {[`${expense.spent_by || 'Unknown submitter'}${expense.spent_by_user_id || expense.spent_by_id ? ` · User #${expense.spent_by_user_id || expense.spent_by_id}` : ''}`, expense.field || 'Farm-wide', day(expense.spent_on), expense.category]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {expense.payment_status === 'not_recorded'
                        ? 'Payment not recorded — needs reconciliation'
                        : `Paid ${money(expense.paid_amount, currency)} · outstanding ${money(expense.outstanding_amount, currency)}`}
                    </p>
                  </button>
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-black ${STATUS_STYLES[expense.review_status || 'pending']}`}>
                    {expense.review_status || 'pending'}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busyId === expense.id}
                      onClick={() => decide(expense, 'reviewed')}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-black text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={busyId === expense.id}
                      onClick={() => openDetail(expense)}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-50"
                    >
                      Review
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-6 rounded-xl border border-slate-200 bg-white">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <h2 className="text-base font-black text-slate-900">Expenses</h2>
            <div className="flex flex-wrap gap-2">
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                placeholder="Search person, job, vendor"
                className="w-48 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                aria-label="Search expenses by person, job, vendor, description or field"
              />
              <input
                value={fieldFilter}
                onChange={(e) => { setFieldFilter(e.target.value); setPage(0); }}
                placeholder="Field or plot"
                className="w-36 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                aria-label="Filter by field or plot"
              />
              <input
                inputMode="numeric"
                value={fieldPlotIdFilter}
                onChange={(e) => { setFieldPlotIdFilter(e.target.value.replace(/\D/g, '')); setPage(0); }}
                placeholder="Field ID"
                className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                aria-label="Filter by exact field ID"
              />
              <input
                inputMode="numeric"
                value={assignmentIdFilter}
                onChange={(e) => { setAssignmentIdFilter(e.target.value.replace(/\D/g, '')); setPage(0); }}
                placeholder="Job ID"
                className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                aria-label="Filter by exact job ID"
              />
              <input
                inputMode="numeric"
                value={workerIdFilter}
                onChange={(e) => { setWorkerIdFilter(e.target.value.replace(/\D/g, '')); setPage(0); }}
                placeholder="Worker ID"
                className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                aria-label="Filter by exact worker record ID"
              />
              <input
                inputMode="numeric"
                value={recipientIdFilter}
                onChange={(e) => { setRecipientIdFilter(e.target.value.replace(/\D/g, '')); setPage(0); }}
                placeholder="Cash recipient ID"
                className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                aria-label="Filter expenses by the recipient linked to the cash request"
              />
              <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} className="rounded-lg border border-slate-200 px-2 py-2 text-sm">
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Approved</option>
                <option value="disputed">Disputed</option>
              </select>
              <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(0); }} className="rounded-lg border border-slate-200 px-2 py-2 text-sm">
                <option value="">All categories</option>
                {['manure', 'compost', 'fertilizer', 'pest_treatment', 'labour', 'transport', 'other'].map((c) => (
                  <option key={c} value={c}>{c.replace('_', ' ')}</option>
                ))}
              </select>
              <select value={paymentStatus} onChange={(e) => { setPaymentStatus(e.target.value); setPage(0); }} className="rounded-lg border border-slate-200 px-2 py-2 text-sm" aria-label="Filter by payment status">
                <option value="">All payment states</option>
                <option value="not_recorded">Not recorded</option>
                <option value="partial">Partially paid</option>
                <option value="paid">Paid</option>
              </select>
              <select value={currencyFilter} onChange={(e) => { setCurrencyFilter(e.target.value); setPage(0); }} className="rounded-lg border border-slate-200 px-2 py-2 text-sm" aria-label="Filter by currency">
                <option value="">All currencies</option>
                {[...new Set([currency, ...(totals.by_currency || []).map((item) => item.currency || '')])].filter(Boolean).map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>
          </header>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Field</th>
                  <th className="px-4 py-2">Person</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-right">Paid</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expenses.map((expense) => (
                  <tr key={expense.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(expense)}>
                    <td className="px-4 py-2 whitespace-nowrap">{day(expense.spent_on)}</td>
                    <td className="px-4 py-2">
                      <p className="font-bold text-slate-900">{expense.description}</p>
                      <p className="text-xs text-slate-500">{expense.category}{expense.job ? ` · ${expense.job}` : ''}</p>
                    </td>
                    <td className="px-4 py-2">{expense.field || 'Farm-wide'}</td>
                    <td className="px-4 py-2">
                      <p>{expense.spent_by || '—'}</p>
                      <p className="text-xs text-slate-500">
                        {expense.spent_by_user_id || expense.spent_by_id ? `User #${expense.spent_by_user_id || expense.spent_by_id}` : 'User ID unavailable'}
                      </p>
                    </td>
                    <td className="px-4 py-2 text-right font-bold">{money(expense.amount, expense.currency || currency)}</td>
                    <td className="px-4 py-2 text-right">
                      {expense.payment_status === 'not_recorded' ? '—' : money(expense.paid_amount, expense.currency || 'Currency unknown')}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-black ${STATUS_STYLES[expense.review_status || 'pending']}`}>
                        {expense.review_status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
                {!loading && expenses.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No expense matches these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs font-semibold text-slate-500">
            <span>{total} expense{total === 1 ? '' : 's'}</span>
            <div className="flex gap-2">
              <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded-md border border-slate-200 px-2 py-1 disabled:opacity-40">Previous</button>
              <button type="button" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-slate-200 px-2 py-1 disabled:opacity-40">Next</button>
            </div>
          </div>
        </section>

        <section className="mt-6 rounded-xl border border-slate-200 bg-white">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div>
              <h2 className="text-base font-black text-slate-900">Transfers</h2>
              <p className="text-xs text-slate-500">Money sent is not money received until the named recipient confirms it.</p>
            </div>
            {canRecordTransfer && (
              <button type="button" onClick={openTransfer} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-black text-white hover:bg-slate-800">
                <PaperAirplaneIcon className="h-4 w-4" /> Record transfer
              </button>
            )}
          </header>
          <ul className="divide-y divide-slate-100">
            {transfers.map((transfer) => (
              <li key={transfer.id} className="flex items-center gap-3 px-4 py-3">
                <PaperAirplaneIcon className="h-5 w-5 text-blue-500" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-slate-900">
                    {money(transfer.amount, transfer.currency || currency)} → {transfer.recipient || 'Unallocated'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {day(transfer.sent_on)} · {transfer.method}
                    {transfer.reference ? ` · ${transfer.reference}` : ''}
                  </p>
                  <p className="text-xs text-slate-500">
                    Recorded by {transfer.recorded_by_name || 'Unknown'}
                    {transfer.recorded_by_user_id ? ` · User #${transfer.recorded_by_user_id}` : ''}
                    {transfer.tenant_id ? ` · Project #${transfer.tenant_id}` : ''}
                  </p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-black ${
                  transfer.confirmed_at ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                }`}>
                  {transfer.confirmed_at ? 'Received' : 'Not confirmed'}
                </span>
              </li>
            ))}
            {!loading && transfers.length === 0 && (
              <li className="px-4 py-6 text-sm text-slate-500">No transfer has been recorded.</li>
            )}
          </ul>
        </section>
      </div>

      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setDetail(null)}>
          <div role="dialog" aria-modal="true" aria-label="Expense detail" className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !detail ? (
              <p className="text-sm text-slate-500">Loading expense…</p>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-black text-slate-900">{detail.expense.description}</h2>
                    <p className="text-sm text-slate-500">
                      {detail.expense.category} · {day(detail.expense.spent_on)} ·{' '}
                      {detail.expense.farm_wide ? 'Farm-wide' : detail.expense.field}
                    </p>
                  </div>
                  <p className="text-2xl font-black text-slate-900">{money(detail.expense.amount, detail.expense.currency || currency)}</p>
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-xs font-bold text-slate-500">Recorded by</dt><dd>{detail.expense.spent_by || '—'}</dd></div>
                  <div><dt className="text-xs font-bold text-slate-500">Vendor</dt><dd>{detail.expense.supplier_name || '—'}</dd></div>
                  <div><dt className="text-xs font-bold text-slate-500">Paid</dt><dd>{money(detail.expense.paid_amount, detail.expense.currency || 'Currency unknown')}</dd></div>
                  <div><dt className="text-xs font-bold text-slate-500">Outstanding</dt><dd>{money(detail.expense.outstanding_amount, detail.expense.currency || 'Currency unknown')}</dd></div>
                </dl>

                <h3 className="mt-4 text-sm font-black text-slate-900">Payment history</h3>
                {detail.payments.length === 0 ? (
                  <p className="rounded-lg bg-amber-50 p-3 text-xs font-semibold text-amber-700">
                    Payment not recorded. This legacy expense needs reconciliation; its approval does not prove cash moved.
                  </p>
                ) : (
                  <ul className="mt-2 divide-y divide-slate-100">
                    {detail.payments.map((p) => (
                      <li key={p.id} className="py-3 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <span>
                            {day(p.paid_at)} · {p.method} · {p.funding_source || 'float'}
                            {p.event_type === 'refund' ? ' · refund' : ''}
                            {p.payer_name ? ` · ${p.payer_name}` : ' · Payer not attributed'}
                            {p.payer_user_id ? ` · User #${p.payer_user_id}` : ''}
                          </span>
                          <span className={`shrink-0 font-black ${p.event_type === 'refund' ? 'text-red-600' : 'text-slate-900'}`}>
                            {p.event_type === 'refund' ? '−' : ''}{money(p.amount, p.currency || currency)}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Recorded by {p.recorded_by_name || 'Unknown'}
                          {p.recorded_by_user_id ? ` · User #${p.recorded_by_user_id}` : ''}
                        </p>
                        {(detail.workers || []).flatMap((worker) => (worker.payment_events || [])
                          .filter((event) => event.id === p.id)
                          .map((event) => ({ worker, event }))).length > 0 && (
                          <p className="mt-1 text-xs text-slate-600">
                            Worker allocation: {(detail.workers || []).flatMap((worker) => (worker.payment_events || [])
                              .filter((event) => event.id === p.id)
                              .map((event) => `${worker.worker_name || `Worker ${worker.worker_record_id}`} ${money(event.amount, worker.currency || 'Currency unknown')}`))
                              .join(' · ')}
                          </p>
                        )}
                        {p.event_type === 'payment' && !p.payer_user_id && (p.funding_source || 'float') === 'float' && (
                          <div className="mt-2 grid gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 md:grid-cols-2">
                            <select value={legacyPayerId} onChange={(e) => setLegacyPayerId(e.target.value)} aria-label="Confirmed historical payer" className="rounded-lg border border-amber-300 bg-white px-2 py-2 text-sm">
                              <option value="">Choose confirmed payer</option>
                              {reviewers.map((r) => <option key={r.id} value={r.id}>{r.full_name} ({String(r.role || '').replace('_', ' ')})</option>)}
                            </select>
                            <input value={legacyReason} onChange={(e) => setLegacyReason(e.target.value)} aria-label="Reconciliation reason" placeholder="Why this attribution is confirmed" className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm" />
                            <button type="button" onClick={() => void attributeLegacyPayment(p.id)} className="rounded-lg bg-amber-800 px-3 py-2 text-xs font-black text-white hover:bg-amber-900 md:col-span-2">Attribute existing payment</button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {detail.lots.length > 0 && (
                  <>
                    <h3 className="mt-4 text-sm font-black text-slate-900">Material lots</h3>
                    <ul className="mt-1 text-sm text-slate-600">
                      {detail.lots.map((lot) => (
                        <li key={lot.id}>{lot.lot_code} · {lot.product_name} · {lot.available_quantity} {lot.unit} available</li>
                      ))}
                    </ul>
                  </>
                )}

                <div className="mt-4 border-t border-slate-100 pt-4">
                  <h3 className="text-sm font-black text-slate-900">Record payment or refund</h3>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                      inputMode="decimal"
                      placeholder="Amount"
                      aria-label="Payment amount"
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                    <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} aria-label="Payment method" className="rounded-lg border border-slate-200 px-2 py-2 text-sm">
                      {['cash', 'mobile_money', 'bank', 'other'].map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
                    </select>
                    <select value={payType} onChange={(e) => setPayType(e.target.value as 'payment' | 'refund')} aria-label="Payment event type" className="rounded-lg border border-slate-200 px-2 py-2 text-sm">
                      <option value="payment">Payment</option>
                      <option value="refund">Refund</option>
                    </select>
                    <select value={paySource} onChange={(e) => setPaySource(e.target.value)} aria-label="Funding source" className="rounded-lg border border-slate-200 px-2 py-2 text-sm">
                      <option value="float">From float</option>
                      <option value="owner_direct">Owner direct</option>
                      <option value="other">Other</option>
                    </select>
                    <select
                      value={payPayerId}
                      onChange={(e) => setPayPayerId(e.target.value)}
                      aria-label="Person whose float paid"
                      disabled={payType === 'refund'}
                      className="col-span-2 rounded-lg border border-slate-200 px-2 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500"
                    >
                      <option value="">Current user</option>
                      {reviewers.map((r) => <option key={r.id} value={r.id}>{r.full_name} ({String(r.role || '').replace('_', ' ')})</option>)}
                    </select>
                    {payType === 'refund' && (
                      <select
                        value={reversesEventId}
                        onChange={(e) => {
                          const selectedId = Number(e.target.value);
                          setReversesEventId(e.target.value);
                          const original = detail.payments.find((p) => p.id === selectedId);
                          if (original) {
                            setPaySource(original.funding_source || 'float');
                            setPayPayerId(original.payer_user_id == null ? '' : String(original.payer_user_id));
                          }
                        }}
                        aria-label="Payment being refunded"
                        className="col-span-2 rounded-lg border border-slate-200 px-2 py-2 text-sm"
                      >
                        <option value="">Choose the payment to reverse</option>
                        {detail.payments.filter((p) => p.event_type === 'payment').map((p) => {
                          const alreadyRefunded = detail.payments
                            .filter((event) => event.event_type === 'refund' && Number(event.reverses_event_id) === p.id)
                            .reduce((sum, event) => sum + Number(event.amount || 0), 0);
                          const remaining = Math.max(0, Number(p.amount || 0) - alreadyRefunded);
                          return remaining > 0.01 ? (
                            <option key={p.id} value={p.id}>{day(p.paid_at)} · {money(remaining, p.currency || currency)} remaining · {p.payer_name || 'Unattributed payer'}</option>
                          ) : null;
                        })}
                      </select>
                    )}
                    <input
                      value={payReference}
                      onChange={(e) => setPayReference(e.target.value)}
                      placeholder="Reference (optional)"
                      aria-label="Payment reference"
                      className="col-span-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  {detail.expense.category === 'labour' && (detail.workers?.length || 0) > 0 && (
                    <section className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <h4 className="text-xs font-black text-slate-800">Allocate this {payType} to workers</h4>
                      <p className="mt-1 text-xs leading-5 text-slate-600">
                        Optional for an unallocated historical payment. If you enter allocations, they must total the payment exactly. Without them, worker balances remain marked for reconciliation.
                      </p>
                      {!detail.worker_allocations_available && (
                        <p className="mt-2 text-xs font-bold text-amber-800">Worker allocation storage is unavailable until migration 091 is applied.</p>
                      )}
                      {detail.worker_allocations_available && payType === 'refund' && !reversesEventId && (
                        <p className="mt-2 text-xs text-slate-600">Choose the original payment first to see its worker allocations.</p>
                      )}
                      {detail.worker_allocations_available && payType === 'refund' && reversesEventId && paymentAllocationWorkers.length === 0 && (
                        <p className="mt-2 text-xs text-slate-600">The selected historical payment has no worker-level allocations to reverse.</p>
                      )}
                      {detail.worker_allocations_available && paymentAllocationWorkers.map((worker) => {
                        const originalAllocation = payType === 'refund'
                          ? (worker.payment_events || []).find((event) => event.id === Number(reversesEventId) && event.event_type === 'payment')
                          : undefined;
                        const unresolvedCurrency = !worker.currency;
                        return (
                          <label key={worker.worker_record_id} className="mt-2 grid grid-cols-[minmax(0,1fr)_9rem] items-center gap-2 text-xs">
                            <span className="min-w-0 truncate font-semibold text-slate-700">
                              {worker.worker_name || `Worker ${worker.worker_record_id}`} · {worker.currency || 'Currency unresolved'}
                              {originalAllocation ? ` · original ${money(originalAllocation.amount, worker.currency || '—')}` : ''}
                            </span>
                            <input
                              value={workerAllocationAmounts[worker.worker_record_id] || ''}
                              onChange={(event) => setWorkerAllocationAmounts((current) => ({
                                ...current,
                                [worker.worker_record_id]: event.target.value,
                              }))}
                              disabled={unresolvedCurrency || (payType === 'refund' && !originalAllocation)}
                              inputMode="decimal"
                              placeholder="Amount"
                              aria-label={`Amount allocated to ${worker.worker_name || `worker ${worker.worker_record_id}`}`}
                              className="rounded-md border border-slate-300 bg-white px-2 py-2 text-sm disabled:bg-slate-100"
                            />
                          </label>
                        );
                      })}
                    </section>
                  )}
                  <button type="button" onClick={recordPayment} className="mt-2 rounded-lg bg-slate-900 px-4 py-2 text-xs font-black text-white hover:bg-slate-800">
                    Save {payType}
                  </button>
                </div>

                {reviewers.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <h3 className="text-sm font-black text-slate-900">Assign reviewer</h3>
                    <div className="mt-2 flex gap-2">
                      <select value={reviewerId} onChange={(e) => setReviewerId(e.target.value)} aria-label="Assigned reviewer" className="flex-1 rounded-lg border border-slate-200 px-2 py-2 text-sm">
                        <option value="">Unassigned</option>
                        {reviewers.map((r) => <option key={r.id} value={r.id}>{r.full_name} ({String(r.role || '').replace('_', ' ')})</option>)}
                      </select>
                      <button type="button" onClick={assignReviewer} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-50">
                        Save
                      </button>
                    </div>
                  </div>
                )}

                {(detail.expense.review_status === 'pending' || detail.expense.review_status === 'disputed') && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <label className="text-xs font-bold text-slate-500" htmlFor="review-reason">
                      Reason (required to dispute or reopen)
                    </label>
                    <textarea
                      id="review-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busyId === detail.expense.id}
                        onClick={() => decide(detail.expense, 'reviewed')}
                        className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        disabled={busyId === detail.expense.id}
                        onClick={() => decide(detail.expense, 'disputed')}
                        className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-black text-red-700 hover:bg-red-100"
                      >
                        Dispute
                      </button>
                      {detail.expense.review_status === 'disputed' && (
                        <button
                          type="button"
                          disabled={busyId === detail.expense.id}
                          onClick={() => decide(detail.expense, 'pending')}
                          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
                        >
                          Reopen
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showTransfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setShowTransfer(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Record transfer">
            <h2 className="text-lg font-black text-slate-900">Record transfer</h2>
            <p className="text-xs text-slate-500">Only the named recipient can confirm receipt.</p>
            <div className="mt-3 space-y-2">
              <select
                value={transferRequestId}
                onChange={(e) => {
                  const id = e.target.value;
                  const request = fundingRequests.find((item) => String(item.id) === id);
                  setTransferRequestId(id);
                  setTransferRecipient(request?.requested_by_user_id ? String(request.requested_by_user_id) : '');
                  setTransferAmount('');
                }}
                aria-label="Link transfer to approved operations cash request"
                className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm"
              >
                <option value="">No linked request</option>
                {fundingRequests.filter((request) => request.status === 'approved' && Number(request.remaining_amount || 0) > 0).map((request) => (
                  <option key={request.id} value={request.id}>
                    #{request.id} · {(request.category || 'operations').replace(/_/g, ' ')} · {money(request.remaining_amount, currency)} remaining
                  </option>
                ))}
              </select>
              <input
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                inputMode="decimal"
                placeholder="Amount"
                aria-label="Transfer amount"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
              <select value={transferRecipient} onChange={(e) => setTransferRecipient(e.target.value)} disabled={!!transferRequestId} aria-label="Transfer recipient" className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm disabled:bg-slate-50">
                <option value="">Choose recipient</option>
                {recipients.map((r) => <option key={r.id} value={r.id}>{r.full_name} ({String(r.role || '').replace('_', ' ')})</option>)}
              </select>
              <input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} aria-label="Transfer date" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <select value={transferMethod} onChange={(e) => setTransferMethod(e.target.value)} aria-label="Transfer method" className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">
                {['mobile_money', 'bank', 'cash'].map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
              </select>
              <input
                value={transferReference}
                onChange={(e) => setTransferReference(e.target.value)}
                placeholder="Reference (optional)"
                aria-label="Transfer reference"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
              <p className="text-xs text-slate-500">Linking an approved request enforces its requester, currency and remaining authorised amount. Sending does not mark receipt as confirmed.</p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowTransfer(false)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700">Cancel</button>
              <button type="button" onClick={recordTransfer} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white">Save transfer</button>
            </div>
          </div>
        </div>
      )}

      {decliningRequest && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setDecliningRequest(null)}>
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="decline-money-request-title"
            className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="decline-money-request-title" className="text-lg font-black text-slate-900">Decline money request?</h2>
            <p className="mt-1 text-sm text-slate-600">
              {money(decliningRequest.amount, currency)} for {(decliningRequest.category || 'other').replace(/_/g, ' ')}
            </p>
            <label htmlFor="money-request-decline-note" className="mt-4 block text-xs font-bold text-slate-600">Note for the requester (optional)</label>
            <textarea
              id="money-request-decline-note"
              rows={3}
              value={declineNote}
              onChange={(event) => setDeclineNote(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setDecliningRequest(null)} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700">Cancel</button>
              <button
                type="button"
                disabled={requestBusyId === decliningRequest.id}
                onClick={() => void decideFundingRequest(decliningRequest, 'declined', declineNote)}
                className="rounded-md bg-red-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
              >{requestBusyId === decliningRequest.id ? 'Saving…' : 'Confirm decline'}</button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default MoneyPage;
